import type { CSSProperties } from 'react';
import { homeAccentName, homeAccentProps, type KidColorName } from './kidColors';
import { useEffect, useState } from 'react';
import { loadJrStaffOwnColor, subscribeJrStaffOwnColor, type JrStaffOwnColor } from './jrStaffReadsClient';
import { useUserDoc } from './users';

/**
 * The colour name the signed-in person's home wears (N18), or null for none.
 *
 * Reads their OWN account doc, never a view-as one. A Jr Staff's email
 * sign-in (role 'ensemble' with a linked studentId) resolves to the colour her
 * dancer home uses, the student record's colour, read through one small Jr
 * Staff reads lane call (ownColor). Everyone else wears the
 * `profileColor` they picked on Account. Palette names only, no hexes.
 */
export function useHomeAccentName(): {
  name: KidColorName | null;
  /** The linked student's id when this is a Jr Staff email sign-in. */
  linkedStudentId: string | null;
  /** False while a linked Jr Staff's colour is still being read. */
  ready: boolean;
} {
  const { realUserDoc } = useUserDoc();
  const linkedStudentId = realUserDoc?.role === 'ensemble'
    && realUserDoc.status === 'active'
    && typeof realUserDoc.studentId === 'string'
    && realUserDoc.studentId.trim()
    ? realUserDoc.studentId.trim()
    : null;
  // One small lane call, cached for the session (loadJrStaffOwnColor), never the
  // students scan. A Jr Staff with a linked student asks once.
  const uid = linkedStudentId ? realUserDoc?.uid ?? null : null;
  const [own, setOwn] = useState<{ uid: string; value: JrStaffOwnColor | null; failed: boolean } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeJrStaffOwnColor(() => setTick((n) => n + 1)), []);
  useEffect(() => {
    if (!uid) return;
    let current = true;
    loadJrStaffOwnColor(uid).then(
      (value) => { if (current) setOwn({ uid, value, failed: false }); },
      () => { if (current) setOwn({ uid, value: null, failed: true }); },
    );
    return () => { current = false; };
  }, [uid, tick]);
  const mine = own && own.uid === uid ? own : null;
  const name = homeAccentName({
    profileColor: realUserDoc?.profileColor ?? null,
    studentId: linkedStudentId,
    studentColor: mine?.value?.color ?? null,
    studentLoaded: !!mine?.value,
    studentFailed: !!mine?.failed,
  });
  const ready = !linkedStudentId || !!mine;
  return { name, linkedStudentId, ready };
}

/** Class and custom properties for the home's accent; `{}` is today's look. */
export function useHomeAccent(): { className?: string; style?: CSSProperties } {
  const { name } = useHomeAccentName();
  return homeAccentProps(name) as { className?: string; style?: CSSProperties };
}
